import { haversineMeters, isRenderableCoordinate } from '../map-layers'

// ── Annotation layer filtering ────────────────────────────────────────────────

/**
 * Drops annotations that cannot be put on the map.
 *
 * `audio_annotations` is written by more than one producer and carries whatever
 * the recording device reported, so its coordinates are untrusted input:
 *
 * - Out of range or non-finite — a swapped lat/lng pair reads as a latitude of
 *   -97 in Austin or -118 in Los Angeles, and MapLibre throws on it.
 * - Exactly 0/0 — what a device with no GPS fix sends. Plottable, but it is the
 *   Gulf of Guinea, so it would drag a fitBounds across the Atlantic.
 *
 * Filtering at the point where rows become layer state, rather than only where
 * markers are built, keeps the count on the layer toggle honest about how many
 * notes the map is actually showing.
 */
export const filterPlottableAnnotations = <
  T extends { location: { latitude: number; longitude: number } },
>(
  annotations: T[],
): T[] =>
  annotations.filter(({ location: { latitude, longitude } }) => {
    if (!isRenderableCoordinate(latitude, longitude)) return false
    return !(latitude === 0 && longitude === 0)
  })

/**
 * Narrow the annotation layer to the date range picked in the annotations dropdown.
 *
 * Both ends are optional — `allowOpenEnd` lets a user pick only a start date, and
 * an absent range means the whole layer.
 *
 * The picker hands back midnight local time for both ends, so `to` has to be
 * widened to the end of its day: comparing against midnight would drop everything
 * recorded on the last day the user chose, which reads as the range being
 * off-by-one rather than as a boundary convention.
 *
 * Takes a structural `{ from?, to? }` rather than {@link AnnotationDateRange}
 * deliberately: that alias is the picker's own type, which requires `from`, while
 * this filter's real domain is "bounded below, above, both, or neither" — a
 * `to`-only range is a meaningful thing to ask it for. The picker's type is
 * assignable to this one, so the state hook still passes its value straight in.
 */
export const filterAnnotationsByDateRange = <
  T extends { recordedAtMs: number },
>(
  annotations: T[],
  range: { from?: Date; to?: Date } | undefined,
): T[] => {
  const fromMs = range?.from ? new Date(range.from).setHours(0, 0, 0, 0) : null
  const toMs = range?.to ? new Date(range.to).setHours(23, 59, 59, 999) : null
  if (fromMs === null && toMs === null) return annotations
  return annotations.filter(
    a =>
      (fromMs === null || a.recordedAtMs >= fromMs) &&
      (toMs === null || a.recordedAtMs <= toMs),
  )
}

/**
 * Narrow the annotation layer to the severities checked in the Annotations dropdown.
 *
 * `knownSeverities` is the fixed set the dropdown renders a checkbox for (today, the
 * keys of `ANNOTATION_SEVERITY_COLORS`). A row whose severity is NOT in that set — one
 * the proto adds after this UI ships — has no checkbox to hide it behind, so it always
 * passes through rather than silently disappearing the moment a new enum value shows up
 * in production; that mirrors the "unknown severity" fallback marker/label in
 * `annotation-labels.ts`.
 */
export const filterAnnotationsBySeverity = <T extends { severity: string }>(
  annotations: T[],
  visibleSeverities: string[],
  knownSeverities: readonly string[],
): T[] => {
  const visible = new Set(visibleSeverities)
  return annotations.filter(
    a => !knownSeverities.includes(a.severity) || visible.has(a.severity),
  )
}

// ── Annotation heatmap ────────────────────────────────────────────────────────

/**
 * Per-note contribution to heat density, by proto severity name.
 *
 * The shared `HEAT_PAINT` weights every point at a flat 0.5, so these are centred
 * on that: a MED note counts exactly as much as a point on the disengagement or
 * PCE heatmaps, and the range around it stays narrow enough that severity tilts
 * the picture without overwhelming it. Three HIGH notes at one intersection should
 * read hotter than three LOW ones, but ten LOW notes still have to out-weigh a
 * single HIGH one — a heatmap that ranked one severe note above a persistent
 * cluster would be answering a different question than "where do notes pile up".
 *
 * NON_PERFORMANT sits with HIGH: it means the SDS did something wrong here, which
 * is the thing worth looking at, even when the drive itself felt unremarkable.
 */
const ANNOTATION_HEAT_WEIGHTS: Record<string, number> = {
  TRIP_ANNOTATION_SEVERITY_LOW: 0.25,
  TRIP_ANNOTATION_SEVERITY_MEDIUM: 0.5,
  TRIP_ANNOTATION_SEVERITY_HIGH: 0.85,
  TRIP_ANNOTATION_SEVERITY_NON_PERFORMANT: 0.85,
}

/**
 * Heat weight for a severity. An unrecognised or absent severity — a value the
 * proto gains later, or a row written before the column existed — falls back to
 * the neutral middle rather than to 0, so the note still shows up on the map
 * instead of silently vanishing from the density.
 */
export const annotationHeatWeight = (severity: string): number =>
  ANNOTATION_HEAT_WEIGHTS[severity] ?? 0.5

/**
 * Build the heatmap source data from the annotations already on the layer.
 *
 * Reuses `filterPlottableAnnotations` for the same reason the marker loop does:
 * MapLibre rejects an out-of-range coordinate, and 0/0 would put a hotspot in the
 * Gulf of Guinea. Emits `[longitude, latitude]` — GeoJSON order, the reverse of
 * how the rows read — which is what the unit test pins.
 */
export const buildAnnotationHeatFeatures = <
  T extends {
    location: { latitude: number; longitude: number }
    severity: string
  },
>(
  annotations: T[],
): GeoJSON.FeatureCollection<GeoJSON.Point, { weight: number }> => ({
  type: 'FeatureCollection',
  features: filterPlottableAnnotations(annotations).map(annotation => ({
    type: 'Feature',
    properties: { weight: annotationHeatWeight(annotation.severity) },
    geometry: {
      type: 'Point',
      coordinates: [
        annotation.location.longitude,
        annotation.location.latitude,
      ],
    },
  })),
})

// ── Annotation route proximity ────────────────────────────────────────────────

/**
 * Radii offered by the "near this route" filter, in metres.
 *
 * 50 m is about one carriageway plus the GPS scatter on a phone recording at the
 * kerb, so it keeps notes made on this road and drops the parallel one. 150 m is
 * the default: it survives a note recorded a few seconds after the driver passed
 * the spot, which is the common case for someone speaking while driving. 500 m and
 * 1 km are for "roughly this corridor" rather than this road.
 */
export const ANNOTATION_PROXIMITY_RADII_M = [50, 150, 500, 1000]

export const DEFAULT_ANNOTATION_PROXIMITY_RADIUS_M = 150

/**
 * Shortest distance in metres from `point` to the segment `start`–`end`.
 *
 * The projection onto the segment happens in a local metre plane (degrees of
 * longitude shrunk by cos(latitude)), because finding the nearest point on a
 * segment is a two-term dot product there instead of spherical trigonometry. Over
 * the tens-to-hundreds of metres a route segment spans, that plane is accurate to
 * well under a metre. The distance actually returned comes from `haversineMeters`,
 * so the number compared against the radius is a real great-circle distance — the
 * plane only decides *where* on the segment to measure from.
 */
const segmentDistanceMeters = (
  point: [number, number],
  start: [number, number],
  end: [number, number],
): number => {
  const lngScale = Math.cos((point[1] * Math.PI) / 180)
  const px = (point[0] - start[0]) * lngScale
  const py = point[1] - start[1]
  const sx = (end[0] - start[0]) * lngScale
  const sy = end[1] - start[1]
  const lengthSq = sx * sx + sy * sy
  // Routing engines do emit duplicate consecutive coordinates; a zero-length
  // segment has no direction to project onto, so measure to its single point.
  // Otherwise clamp to [0, 1] so the foot stays *on* the segment — an unclamped
  // projection measures to the infinite line, which would call a note "near the
  // route" when it sits a mile past the last waypoint but in line with the road.
  const t =
    lengthSq === 0
      ? 0
      : Math.max(0, Math.min(1, (px * sx + py * sy) / lengthSq))
  return haversineMeters(point, [
    start[0] + (end[0] - start[0]) * t,
    start[1] + (end[1] - start[1]) * t,
  ])
}

/**
 * Shortest distance in metres from a `[lng, lat]` point to a route polyline.
 *
 * Measured to the nearest point on the nearest *segment*, not to the nearest
 * vertex: routing output is dense in curves and sparse on straights, so
 * vertex-only distance would report a note beside a long straight block as
 * hundreds of metres away.
 *
 * An empty route is infinitely far — there is nothing to be near.
 */
export const distanceToRouteMeters = (
  point: [number, number],
  route: [number, number][],
): number => {
  if (route.length === 0) return Number.POSITIVE_INFINITY
  if (route.length === 1) return haversineMeters(point, route[0])
  let nearest = Number.POSITIVE_INFINITY
  for (let i = 0; i < route.length - 1; i++) {
    nearest = Math.min(
      nearest,
      segmentDistanceMeters(point, route[i], route[i + 1]),
    )
  }
  return nearest
}

/**
 * Keep only the annotations within `radiusMeters` of the route polyline.
 *
 * Inert while there is no route yet: blanking the layer as someone places their
 * first waypoint would look like the notes had failed to load, and "within 150 m
 * of nothing" has no useful answer. Callers pass the polyline they already have,
 * so the pins and the heatmap narrow from the same predicate.
 */
export const filterAnnotationsNearRoute = <
  T extends { location: { latitude: number; longitude: number } },
>(
  annotations: T[],
  route: [number, number][],
  radiusMeters: number,
): T[] => {
  if (route.length === 0) return annotations
  // A cheap box test first: the route's bounding box grown by the radius. Any
  // note outside it is further than the radius from every segment, so the
  // per-segment measure only runs for the few notes near the route. Without
  // it, a metro's thousands of notes times a long route's segments made
  // ticking this filter hang the menu.
  let west = Number.POSITIVE_INFINITY
  let south = Number.POSITIVE_INFINITY
  let east = Number.NEGATIVE_INFINITY
  let north = Number.NEGATIVE_INFINITY
  for (const [lng, lat] of route) {
    west = Math.min(west, lng)
    east = Math.max(east, lng)
    south = Math.min(south, lat)
    north = Math.max(north, lat)
  }
  // Generous on purpose: a degree of latitude is at least 110.5 km, and the
  // longitude margin is scaled by the route's highest latitude, where a degree
  // of longitude is shortest.
  const latMargin = radiusMeters / 110_000
  const maxAbsLat = Math.min(89, Math.max(Math.abs(south), Math.abs(north)))
  const lngMargin = latMargin / Math.cos((maxAbsLat * Math.PI) / 180)
  return annotations.filter(
    annotation =>
      annotation.location.latitude >= south - latMargin &&
      annotation.location.latitude <= north + latMargin &&
      annotation.location.longitude >= west - lngMargin &&
      annotation.location.longitude <= east + lngMargin &&
      distanceToRouteMeters(
        [annotation.location.longitude, annotation.location.latitude],
        route,
      ) <= radiusMeters,
  )
}
